import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import * as wasm from 'sched';
import { v4 as uuidv4 } from 'uuid';
import { useDatabase } from './useDatabase';
import { useTemplateCache } from './useTemplateCache';
import { WorkspaceContext } from '../context/WorkspaceContext';

export interface DraftAllocation {
  entity: string;
  personId: string;
  /** Effort in tenths of a day. 10 = 1 full day. */
  effort: number;
  whenStart: string; // YYYY-MM-DD
  whenEnd: string;   // YYYY-MM-DD
}

export interface PlanningGridState {
  result: wasm.PlanningGridResult | null;
  loading: boolean;
  error: string | null;
  draftAllocations: DraftAllocation[];
  addDraft: (alloc: DraftAllocation) => void;
  removeDraft: (index: number) => void;
  acceptSuggestion: (personId: string, epochIdx: number, suggestion: wasm.PlannedAllocationDto) => void;
  addAllocationForCell: (personId: string, epochIdx: number, entityId: string, effort: number) => void;
  removeDraftForCell: (personId: string, epochIdx: number, draftIdx: number) => void;
  commitDrafts: () => Promise<void>;
  discardDrafts: () => void;
}

export interface PlanningRhythmConfig {
  interval_days: number;
  start_day: string; // chrono::Weekday: "Mon", "Tue", etc.
}

export function usePlanningGrid(
  teamId: string | null,
  startDate: string,
  endDate: string,
  rhythm?: PlanningRhythmConfig | null,
): PlanningGridState {
  const { database, applyCommand } = useDatabase();
  const { safeData } = useContext(WorkspaceContext);
  const templateCache = useTemplateCache();
  const [result, setResult] = useState<wasm.PlanningGridResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftAllocations, setDraftAllocations] = useState<DraftAllocation[]>([]);

  // Debounce timer
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Rebuild planning grid
  const rebuild = useCallback(() => {
    if (!teamId || !database || !templateCache?.wrappedCache) return;

    setLoading(true);
    try {
      const drafts = draftAllocations.map((d) => ({
        entity: d.entity,
        person_id: d.personId,
        effort: d.effort,
        when_start: d.whenStart,
        when_end: d.whenEnd,
      }));

      const gridResult = wasm.build_team_planning_suggestions(
        database,
        templateCache.wrappedCache,
        teamId,
        startDate,
        endDate,
        rhythm ?? null,
        drafts,
      );

      setResult(gridResult as unknown as wasm.PlanningGridResult);
      setError(null);
    } catch (e: any) {
      console.error('Planning grid error:', e);
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [teamId, database, startDate, endDate, rhythm?.interval_days, rhythm?.start_day, draftAllocations, templateCache?.wrappedCache]);

  // Debounced rebuild
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(rebuild, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [rebuild]);

  const addDraft = useCallback((alloc: DraftAllocation) => {
    setDraftAllocations((prev) => [...prev, alloc]);
  }, []);

  const removeDraft = useCallback((index: number) => {
    setDraftAllocations((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const acceptSuggestion = useCallback(
    (personId: string, dayIdx: number, suggestion: wasm.PlannedAllocationDto) => {
      if (!result) return;
      const days: any[] = (result as any).days ?? (result as any).epochs ?? [];
      const day = days[dayIdx];
      if (!day) return;

      const entityRef = suggestion.entity;
      let entityStr: string;
      if ('id' in entityRef && entityRef.type === 'Task') {
        entityStr = (entityRef as any).id;
      } else if (entityRef.type === 'WorkflowStep') {
        entityStr = `step:${(entityRef as any).instance_id}:${(entityRef as any).step_id}`;
      } else {
        entityStr = JSON.stringify(entityRef);
      }

      const effort = (suggestion as any).effort ?? (suggestion as any).hours ?? 0;
      const date = day.date ?? day.start_date;

      addDraft({
        entity: entityStr,
        personId,
        effort,
        whenStart: date,
        whenEnd: date,
      });
    },
    [result, addDraft],
  );

  const commitDrafts = useCallback(async () => {
    if (!result) return;

    for (const draft of draftAllocations) {
      const epoch = (() => {
        if (draft.whenStart === draft.whenEnd) {
          return { type: 'Day', day: draft.whenStart };
        }
        return {
          type: 'Days',
          start_inclusive: draft.whenStart,
          end_inclusive: draft.whenEnd,
        };
      })();

      const command: wasm.DatabaseCommand = {
        type: 'IncrementResourceAllocation',
        id: uuidv4(),
        entity: draft.entity,
        person_id: draft.personId,
        increment: draft.effort,
        when: epoch as wasm.SchedulingEpoch,
      };

      await applyCommand(command);
    }

    setDraftAllocations([]);
  }, [draftAllocations, result, applyCommand]);

  const addAllocationForCell = useCallback(
    (personId: string, dayIdx: number, entityId: string, effort: number) => {
      if (!result) return;
      const days: any[] = (result as any).days ?? (result as any).epochs ?? [];
      const day = days[dayIdx];
      if (!day) return;
      const date = day.date ?? day.start_date;

      addDraft({
        entity: entityId,
        personId,
        effort,
        whenStart: date,
        whenEnd: date,
      });
    },
    [result, addDraft],
  );

  const removeDraftForCell = useCallback(
    (personId: string, dayIdx: number, draftIdx: number) => {
      if (!result) return;
      const days: any[] = (result as any).days ?? (result as any).epochs ?? [];
      const day = days[dayIdx];
      if (!day) return;
      const date = day.date ?? day.start_date;

      let count = 0;
      const globalIdx = draftAllocations.findIndex((d) => {
        if (d.personId === personId && d.whenStart === date && d.whenEnd === date) {
          if (count === draftIdx) return true;
          count++;
        }
        return false;
      });

      if (globalIdx >= 0) {
        removeDraft(globalIdx);
      }
    },
    [result, draftAllocations, removeDraft],
  );

  const discardDrafts = useCallback(() => {
    setDraftAllocations([]);
  }, []);

  return {
    result,
    loading,
    error,
    draftAllocations,
    addDraft,
    removeDraft,
    acceptSuggestion,
    addAllocationForCell,
    removeDraftForCell,
    commitDrafts,
    discardDrafts,
  };
}
